import { useState, useEffect, useRef } from 'react';
import { animate } from 'animejs';
import { useReducedMotion } from './useReducedMotion';

interface AnimatedCounterOptions {
    duration?: number;
    isInView?: boolean;
    decimals?: number;
    prefix?: string;
    suffix?: string;
}

interface AnimatedCounterResult {
    value: number;
    displayValue: string;
    hasAnimated: boolean;
}

export function useAnimatedCounter(
    target: number,
    options: AnimatedCounterOptions = {}
): AnimatedCounterResult {
    const {
        duration = 2000,
        isInView = true,
        decimals,
        prefix = '',
        suffix = '',
    } = options;

    const [value, setValue] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const reducedMotion = useReducedMotion();
    const animationRef = useRef<ReturnType<typeof animate> | null>(null);

    const decimalPlaces = decimals ?? (target % 1 !== 0 ? 1 : 0);

    useEffect(() => {
        if (!isInView || hasAnimated) return;

        if (reducedMotion) {
            setValue(target);
            setHasAnimated(true);
            return;
        }

        const animationTarget = { value: 0 };

        animationRef.current = animate(animationTarget, {
            value: target,
            duration,
            ease: 'outExpo',
            onUpdate: () => {
                setValue(animationTarget.value);
            },
            onComplete: () => {
                setValue(target);
                setHasAnimated(true);
            },
        });

        return () => {
            if (animationRef.current) {
                animationRef.current.pause();
            }
        };
    }, [target, duration, isInView, hasAnimated, reducedMotion, decimalPlaces]);

    const displayValue = `${prefix}${decimalPlaces > 0
            ? value.toFixed(decimalPlaces)
            : Math.floor(value).toLocaleString()
        }${suffix}`;

    return { value, displayValue, hasAnimated };
}

export default useAnimatedCounter;
